import { Container, PullQuote, CTAStrip, ImageBlock } from "@/components/Sections";
import { IMAGES } from "@/lib/content";

export default function About() {
  return (
    <div data-testid="page-about" className="bg-[#F6F1E6]">
      <section className="pt-40 md:pt-52 pb-24">
        <Container>
          <p className="overline mb-6">Section 10 — About</p>
          <h1
            data-testid="about-title"
            className="display-xxl text-5xl md:text-7xl lg:text-[112px] mb-10"
          >
            About
            <br />
            <span className="italic text-[#3F5132]">Carbon on Country.</span>
          </h1>
          <p className="text-[#4F5A48] text-xl md:text-2xl max-w-3xl leading-relaxed font-serif italic">
            The restoration economy needs a skilled workforce. Indigenous
            ranger programs already undertake many of the activities required by
            carbon, biodiversity and restoration projects.
          </p>
        </Container>
      </section>

      <section className="border-t border-[#1F2A1B]/10 py-24 md:py-32">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            <div className="lg:col-span-5">
              <ImageBlock
                src={IMAGES.outback1}
                alt="Australian outback landscape"
                caption="Carbon on Country"
                className="aspect-[3/4]"
                testid="about-image-1"
              />
            </div>
            <div className="lg:col-span-7 space-y-6 text-[#4F5A48] text-lg leading-relaxed">
              <p>
                Carbon on Country was created from a simple observation.
              </p>
              <p>
                Across Australia, Indigenous ranger programs are already
                undertaking many of the activities required by carbon projects,
                biodiversity programs and restoration initiatives.
              </p>
              <ul className="space-y-3 my-8">
                {[
                  "Monitoring landscapes.",
                  "Managing fire.",
                  "Collecting ecological information.",
                  "Working with Traditional Owners.",
                  "Recording environmental change.",
                  "Supporting long-term stewardship of Country.",
                ].map((line) => (
                  <li
                    key={line}
                    className="font-serif italic text-2xl text-[#1F2A1B] border-l-2 border-[#C45A3C]/40 pl-5"
                  >
                    {line}
                  </li>
                ))}
              </ul>
              <p>
                At the same time, environmental markets are creating demand for
                new professions that rely on many of these same capabilities.
              </p>
              <p className="font-serif italic text-2xl md:text-3xl text-[#3F5132]">
                Carbon on Country seeks to bridge these two worlds.
              </p>
            </div>
          </div>
        </Container>
      </section>

      <section className="border-t border-[#1F2A1B]/10 py-24 md:py-32 bg-[#EBE3D0]">
        <Container>
          <div className="max-w-4xl">
            <p className="overline mb-6">Our focus</p>
            <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] mb-12 text-[#1F2A1B]">
              Our focus is not simply on carbon projects.
              <br />
              <span className="italic text-[#3F5132]">Our focus is on people.</span>
            </h2>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-0">
              {[
                "Building practical skills",
                "Creating employment pathways",
                "Supporting long-term capability",
                "Strengthening Indigenous participation in the restoration economy",
              ].map((line, i) => (
                <li
                  key={line}
                  className="flex items-baseline gap-5 py-6 border-b border-[#1F2A1B]/10"
                >
                  <span className="section-number">0{i + 1}</span>
                  <span className="text-[#1F2A1B] text-lg md:text-xl leading-snug">
                    {line}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <PullQuote testid="about-quote-north-star">
            Environmental investment should not only restore landscapes. It
            should leave behind stronger communities, greater capability and
            opportunities for future generations.
          </PullQuote>
        </Container>
      </section>

      <section className="border-t border-[#1F2A1B]/10 py-24 md:py-32">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-7">
              <p className="overline mb-6">Environmental infrastructure</p>
              <h2 className="font-serif text-3xl md:text-5xl leading-[1.05] mb-8 text-[#1F2A1B]">
                Infrastructure is often understood as roads, buildings, power.
                <br />
                <span className="italic text-[#3F5132]">
                  Environmental infrastructure is different.
                </span>
              </h2>
              <div className="flex flex-wrap gap-2 mt-8">
                {["People", "Skills", "Monitoring systems", "Data", "Knowledge"].map(
                  (t) => (
                    <span
                      key={t}
                      className="text-xs tracking-[0.22em] uppercase text-[#1F2A1B] border border-[#C45A3C]/60 px-4 py-2"
                    >
                      {t}
                    </span>
                  )
                )}
              </div>
            </div>
            <div className="lg:col-span-5">
              <ImageBlock
                src={IMAGES.bushland}
                alt="Australian bushland"
                caption="Infrastructure of Country"
                className="aspect-[4/3]"
                testid="about-image-2"
              />
            </div>
          </div>
        </Container>
      </section>

      <CTAStrip
        heading="It leaves behind knowledge, skills and opportunity."
        body="That is the work of Carbon on Country."
        label="Get in touch"
        testid="about-cta-strip"
      />
    </div>
  );
}
